import React, { useState, useCallback, useRef, useEffect, useContext } from 'react';
import { geoPath, geoMercator } from 'd3-geo';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import RiskOverviewSectionLoadingState from '/src/Components/RiskOverviewSectionLoadingState/RiskOverviewSectionLoadingState';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import RiskOverviewSectionHeader from '../RiskOverviewSectionHeader/RiskOverviewSectionHeader';
import RiskOverviewClimateIcon from '/src/Components/RiskOverviewClimateIcon/RiskOverviewClimateIcon';
import './style.css';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import MapVisualization from '/src/Components/MapVisualization/MapVisualization';
import { RiskOverviewContext } from '../RiskOverview';
import { AreaDataContext } from '../../PropertyExplorer';
import SeeMoreClimateDetails from './SeeMoreClimateDetails/SeeMoreClimateDetails';
import CTAButton from '/src/Components/CTAButton/CTAButton';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import { usePropertyGaLogging } from '/src/contexts/PropertyGaLoggingContext';
// Constants outside component
const NRI_VALUES_TO_COLLECT = [
    'EVNTS', 'AFREQ', 'EXPB', 'EXPE', 'EXPT', 'EXP_AREA',
    'HLRB', 'HLRP', 'HLRR', 'EALB', 'EALP', 'EALPE',
    'EALS', 'EALR', 'ALRB', 'ALRP', 'ALR_NPCTL',
    'RISKV', 'RISKR', 'RISKS'
];

const RISK_DICTIONARY = [
    {
        quoll_id: '1',
        type: 'Cold',
        icon_black: '48',
        icon_white: '64',
        nri_code: 'CWAV'
    },
    {
        quoll_id: '2',
        type: 'Severe Thunderstorm',
        icon_black: '49',
        icon_white: '65'
    },
    {
        quoll_id: '3',
        type: 'Drought',
        icon_black: '50',
        icon_white: '66',
        nri_code: 'DRGT'
    },
    {
        quoll_id: '4',
        type: 'Wildfire',
        icon_black: '52',
        icon_white: '68',
        nri_code: 'WFIR'
    },
    {
        quoll_id: '5',
        type: 'Flood',
        icon_black: '53',
        icon_white: '69'
    },
    {
        quoll_id: '6',
        type: 'Hail',
        icon_black: '54',
        icon_white: '70',
        nri_code: 'HAIL'
    },
    {
        quoll_id: '7',
        type: 'Hurricane',
        icon_black: '55',
        icon_white: '71',
        nri_code: 'HRCN'
    },
    {
        quoll_id: '8',
        type: 'Landslide',
        icon_black: '56',
        icon_white: '72',
        nri_code: 'LNDS'
    },
    {
        quoll_id: '9',
        type: 'Lightning',
        icon_black: '57',
        icon_white: '73',
        nri_code: 'LTNG'
    },
    {
        quoll_id: '10',
        type: 'Sinkhole',
        icon_black: '59',
        icon_white: '75'
    },
    {
        quoll_id: '11',
        type: 'Snow',
        icon_black: '60',
        icon_white: '76',
        nri_code: 'WNTW'
    },
    {
        quoll_id: '12',
        type: 'Tornado',
        icon_black: '61',
        icon_white: '77',
        nri_code: 'TRND'
    },
    {
        quoll_id: '13',
        type: 'Tsunami',
        icon_black: '62',
        icon_white: '78',
        nri_code: 'TSUN'
    },
    {
        quoll_id: '14',
        type: 'Wind',
        icon_black: '63',
        icon_white: '79',
        nri_code: 'SWND'
    },
    {
        quoll_id: '15',
        type: 'Earthquake',
        icon_black: '51',
        icon_white: '67',
        nri_code: 'ERQK'
    },
    {
        quoll_id: '16',
        type: 'Mold',
        icon_black: '58',
        icon_white: '74'
    },
    {
        quoll_id: '17',
        type: 'Heat Wave',
        icon_black: '740',
        icon_white: '739',
        nri_code: 'HWAV'
    },
    {
        type: 'Coastal Flooding',
        icon_black: '737',
        icon_white: '738',
        nri_code: 'CFLD'
    },
    {
        type: 'Riverine Flooding',
        icon_black: '736',
        icon_white: '735',
        nri_code: 'RFLD'
    }
];

const RISK_ORDER = {
    'Very High': 6,
    'Relatively High': 5,
    'High': 4,
    'Moderate': 3,
    'Relatively Low': 1,
    'Low': 0.5,
    'Very Low': 0
};

const ClimateInYourArea = ({ propertyData, title }) => {
    const county_name = propertyData?.data?.property_details?.county
    const intro_sentence = `It is important to have a clear picture of what is happening around you as the profile of the area plays a major role as we start to look at the cost implications of climate for this home. Let's start by looking at the climate risk profile of ${county_name}. `

    const { geoData, contentLoaded } = useContext(AreaDataContext);
    const { deviceType } = useContext(WindowSizeContext);
    // Get the portalData from the AddressSearchContext
    const { portalData, LOG_GA_EVENT } = useContext(AddressSearchContext);
    const { handleLoggingGaEventFromProperty } = usePropertyGaLogging();

    // if (!contentLoaded) {
    //     return <RiskOverviewSectionLoadingState title={title} intro_sentence={intro_sentence} />;
    // }


    // console.log('geoData', geoData);
    const fipsGEOData = geoData.fips
    const stateGEOData = geoData.state

    // console.log('fipsGEOData', fipsGEOData);

    // 1. All useState hooks first
    const [countyData, setCountyData] = useState(fipsGEOData);
    const [stateData, setStateData] = useState(stateGEOData);
    const [parentGeoJson, setParentGeoJson] = useState(null);
    const [childGeoJson, setChildGeoJson] = useState(null);
    const [nriValues, setNriValues] = useState([]);
    const [hoveredRiskCode, setHoveredRiskCode] = useState(null);
    const [femaDeclaredDisasterCount, setFemaDeclaredDisasterCount] = useState(0);
    const [seeMore, setSeeMore] = useState(false);

    useEffect(() => {
        if (geoData) {
            const newCountyData = geoData.fips
            const newStateData = geoData.state
            setCountyData(newCountyData)
            setStateData(newStateData)

            const femaDeclaredDisastersCount = newCountyData?.fema_data?.summary?.disaster_declarations_count || 0;
            setFemaDeclaredDisasterCount(femaDeclaredDisastersCount);
        }
    }, [geoData]);

    // useEffect to log when seeMore is toggled to true
    useEffect(() => {
        if (seeMore) {
            handleLoggingGaEventFromProperty('fema_disaster_breakdown_view');
        }
    }, [seeMore]);

    // 2. All useCallback hooks together
    const handleMouseEnter = useCallback((riskCode) => {
        const timer = setTimeout(() => {
            setHoveredRiskCode(riskCode);
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    const handleMouseLeave = useCallback(() => {
        const timer = setTimeout(() => {
            setHoveredRiskCode(null);
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    const processNriValues = useCallback((countyData) => {
        if (!countyData) return [];
        const processedNriValues = [];
        RISK_DICTIONARY.forEach(item => {
            if (item.nri_code) {
                const riskData = {
                    type: item.type,
                    nri_code: item.nri_code,
                    icon_black: item.icon_black,
                    icon_white: item.icon_white
                };

                NRI_VALUES_TO_COLLECT.forEach(valueCode => {
                    const key = `${item.nri_code}_${valueCode}`;
                    let value = countyData[key];

                    if ((valueCode === 'EALR' || valueCode === 'HLRR' || valueCode === 'RISKR') &&
                        (value === null || value === undefined || value?.toLowerCase().includes('insufficient'))) {
                        value = "Very Low";
                    }

                    if (value === "Relatively Moderate") {
                        value = "Moderate";
                    }

                    riskData[valueCode] = value;
                });

                processedNriValues.push(riskData);
            }
        });

        return processedNriValues.sort((a, b) => {
            const riskA = RISK_ORDER[a.RISKR] || 0;
            const riskB = RISK_ORDER[b.RISKR] || 0;
            return riskB - riskA;
        });
    }, []);

    // 3. All useEffect hooks
    // useEffect(() => {
    //     if (!propertyData?.data?.property_details) return;
    //     const qgdData = localStorage.getItem('qgd');
    //     const qgdDataJson = JSON.parse(qgdData);
    //     if (!qgdDataJson) return;

    //     const { fips, state_short } = propertyData.data.property_details;
    //     setCountyData(qgdDataJson.fips[fips]);
    //     setStateData(qgdDataJson.state[state_short]);
    // }, [propertyData]);

    useEffect(() => {
        if (!countyData?.geometry?.geo_shape || !stateData?.geometry?.st_asgeojson) return;

        // console.log('UPDATED GEO DATA FROM CLIMATE IN YOUR AREA');
        setParentGeoJson(stateData.geometry.st_asgeojson);
        setChildGeoJson(countyData.geometry.geo_shape);
    }, [countyData, stateData]);

    useEffect(() => {
        if (!countyData) return;
        const processed = processNriValues(countyData);
        setNriValues(processed);
    }, [countyData, processNriValues]);

    // 4. Loading check
    if (!countyData || !stateData || !parentGeoJson || !childGeoJson || !nriValues.length) {
        return <div>Loading...</div>;
    }

    // 5. Rest of the render logic
    const address_id = propertyData.address_id;


    // const fips = propertyData.data.property_details.fips
    // const state = propertyData.data.property_details.state_short
    // const census_tract = propertyData.data.property_details.census_tract

    // Get the county shape geometry from the countyData
    const countyShape = countyData.geometry?.geo_shape;
    const stateShape = stateData.geometry?.st_asgeojson;
    const censusTractShapeCoordinates = countyData.geometry?.coordinates?.geometry;
    const censusTractShape = {
        type: 'Feature',
        geometry: {
            type: 'Polygon',
            coordinates: [censusTractShapeCoordinates]
        }
    };

    // Load the icons from local storage
    // const icons = portalData.icons;
    // const iconsJson = JSON.parse(icons);

    // Get the general NRI datapoint for the county
    const nri_building_value = countyData?.BUILDVALUE;
    const nri_area = countyData?.AREA;
    const nri_risk_rating = countyData?.RISK_RATING;
    const nri_risk_score = countyData?.RISK_SCORE;
    const nri_risk_state_pctl = countyData?.RISK_SPCTL;
    const nri_social_vulnerability_rating = countyData?.SOVI_RATING;
    const nri_social_vulnerability_score = countyData?.SOVI_SCORE;
    const nri_social_vulnerability_state_pctl = countyData?.SOVI_SPCTL;
    const nri_community_resilience_rating = countyData?.RESL_RATNG;
    const nri_community_resilience_score = countyData?.RESL_SCORE;
    const nri_community_resilience_state_pctl = countyData?.RESL_SPCTL;

    // Get risk_rating for county if the nri_risk_rating is not available
    let countyRiskRating;
    let countyRiskRatingLevel;

    if (nri_risk_rating) {
        countyRiskRating = nri_risk_rating;
    } else {
        if (nri_risk_score > 99) {
            countyRiskRating = 'Very High';
            countyRiskRatingLevel = 'high';
        } else if (nri_risk_score > 95) {
            countyRiskRating = 'High';
            countyRiskRatingLevel = 'high';
        } else if (nri_risk_score > 80) {
            countyRiskRating = 'Moderate';
            countyRiskRatingLevel = 'moderate';
        } else if (nri_risk_score > 50) {
            countyRiskRating = 'Relatively Low';
            countyRiskRatingLevel = 'low';
        } else if (nri_risk_score > 10) {
            countyRiskRating = 'Low';
            countyRiskRatingLevel = 'low';
        } else {
            countyRiskRating = 'Very Low';
            countyRiskRatingLevel = 'low';
        }
    }




    // Get the currently hovered risk data
    const hoveredRisk = hoveredRiskCode
        ? nriValues.find(risk => risk.nri_code === hoveredRiskCode)
        : null;


    const section_title = "What is climate change doing in your area?"

    // Check if we have valid GeoJSON data before attempting to render the map
    const hasValidGeoData = parentGeoJson && childGeoJson;

    const learnMoreAboutFemaDisastersText = `Learn More About FEMA Declared Disasters in the Area Over the Last 10 Years`

    return (
        <div className="tab-content-container">
            <RiskOverviewSectionHeader title={title} />
            <p className="text-align-center" dangerouslySetInnerHTML={{ __html: intro_sentence }} />
            <div className={`climate-in-your-area-container ${deviceType}`}>
                <div className="climate-in-your-area-map-text-container">
                    <div className="climate-in-your-area-map-container">
                        {(parentGeoJson && childGeoJson) && (
                            <MapVisualization
                                parentGeoJson={parentGeoJson}
                                childGeoJson={childGeoJson}
                                key="ClimateInYourArea"
                            />
                        )}
                        <span className="climate-in-your-area-map-label">{county_name}</span>
                        <div className={`climate-in-your-area-risk-rating-container ${countyRiskRatingLevel}`}>
                            <span className="climate-in-your-area-risk-rating">{countyRiskRating} Climate Risk</span>
                        </div>
                        <span className="data-source-citation">Source: FEMA National Risk Index</span>
                    </div>
                    <CurlyBraceDivider direction={deviceType === 'desktop' ? 'right' : 'down'} color="grey" />
                    <div className="climate-in-your-area-risks-grid">
                        {nriValues.map((risk) => {

                            const risk_level_lower_case = risk.RISKR.toLowerCase();

                            const notSelectedWords = ['low', 'applicable', 'no']

                            let notSelected = null;
                            // If 'low' in risk.RISKR (to lower case) then notSelected = 'not-selected'
                            if (notSelectedWords.some(word => risk_level_lower_case.includes(word))) {
                                notSelected = 'not-selected';
                            }

                            return (
                                <RiskOverviewClimateIcon
                                    key={risk.nri_code}
                                    id={risk.nri_code}
                                    risk_level={risk.RISKR}
                                    icon_white={risk.icon_white}
                                    icon_black={risk.icon_black}
                                    risk_name={risk.type}
                                    handleMouseEnter={handleMouseEnter}
                                    handleMouseLeave={handleMouseLeave}
                                    notSelected={notSelected}
                                    requiresLookup={true}
                                />
                            )
                        })}
                    </div>
                </div>
            </div>
            {femaDeclaredDisasterCount > 0 && (
                <div className={`climate-in-your-area-see-more-container ${deviceType}`}>
                    <CTAButton
                        text={seeMore ? 'Hide' : learnMoreAboutFemaDisastersText}
                        onClick={() => seeMore ? setSeeMore(false) : setSeeMore(true)}
                        category={'fema-disasters-breakdown'}
                    />
                    {seeMore && (
                        <>
                            <CurlyBraceDivider direction="down" color="grey" />
                            <SeeMoreClimateDetails countyName={county_name} />
                        </>

                    )}
                </div>
            )}
        </div>
    );
};

export default ClimateInYourArea;